<script setup lang="ts">
import type { ConditionalRule } from '#shared/types/forms'
import { validateRijksregisternummer } from '#shared/utils/rijksregisternummer'

// Types from cart store
interface CampFormQuestion {
  id: string
  questionType: string
  label: string
  placeholder?: string
  helpText?: string
  description?: string
  isRequired: boolean
  options?: string[]
  validationRules?: unknown
  conditionalRule?: unknown
  orderIndex: number
  content?: string
  titleSize?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  url?: string
  linkText?: string
  imageAlt?: string
}

interface CampForm {
  id: string
  name: string
  description?: string
  is_required: boolean
  order_index: number
  questions: CampFormQuestion[]
}

interface FormResponse {
  question_id: string
  answer: string | number | boolean | null | string[]
}

interface Props {
  forms: CampForm[]
  participantName?: string
  participantIndex: number
  modelValue: FormResponse[]
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  participantName: '',
})

const emit = defineEmits<{
  'update:modelValue': [value: FormResponse[]]
}>()

// Local form data reactive object
const formData = ref<Record<string, FormResponse['answer']>>({})

// Validation errors for rijksregisternummer fields
const rijksregisterErrors = ref<Record<string, string | null>>({})

// Track which rijksregisternummer fields are marked as "no RRN"
const noRRNFields = ref<Record<string, boolean>>({})

const touchedFields = ref<Record<string, boolean>>({})
const showValidationErrors = ref(false)

// Check if a content element type
const isContentElement = (type: string) => {
  return ['title', 'paragraph', 'link', 'image', 'separator'].includes(type)
}

// Check if a question belongs to base participant fields
const isBaseFieldQuestion = (label?: string | null) => {
  const normalized = (label || '').toLowerCase().trim()

  return (
    normalized.includes('voornaam')
    || normalized.includes('achternaam')
    || normalized.includes('geboorte')
  )
}

const getSelectValue = (questionId: string): string | null => {
  const value = formData.value[questionId]

  if (value === null || value === undefined)
    return null

  return typeof value === 'string' ? value : String(value)
}

const setSelectValue = (questionId: string, value: string | null) => {
  formData.value[questionId] = value
}

// Initialize form data from modelValue
const initializeFormData = () => {
  // Initialize all questions with empty string by default to avoid undefined
  props.forms.forEach(form => {
    form.questions.forEach(question => {
      if (!isContentElement(question.questionType)) {
        // Set default value based on question type
        if (question.questionType === 'checkbox')
          formData.value[question.id] = []
        else if (question.questionType === 'date')
          formData.value[question.id] = null
        else
          formData.value[question.id] = ''
      }
    })
  })

  // Then populate with actual values from modelValue
  props.modelValue.forEach(response => {
    formData.value[response.question_id] = response.answer
  })
}

onMounted(() => {
  initializeFormData()
})

// Watch for external changes to modelValue
watch(() => props.modelValue, newValue => {
  // If the new value is empty or significantly different, reset all form data
  if (newValue.length === 0) {
    initializeFormData()
  }
  else {
    // Update existing values
    newValue.forEach(response => {
      formData.value[response.question_id] = response.answer
    })
  }
})

// Watch for changes to props.forms to reinitialize
watch(() => props.forms, () => {
  initializeFormData()
}, { deep: true })

// Watch for changes in formData and emit updates
watch(formData, newData => {
  // Get all question IDs from forms
  const allQuestionIds = new Set<string>()

  props.forms.forEach(form => {
    form.questions.forEach(q => {
      // Only track actual input fields, not content elements
      if (!['title', 'paragraph', 'link', 'image', 'separator'].includes(q.questionType) && !isBaseFieldQuestion(q.label)) {
        allQuestionIds.add(q.id)
      }
    })
  })

  // Include ALL question responses, even if empty
  // This ensures parent can properly validate required fields
  const responses: FormResponse[] = Array.from(allQuestionIds).map(questionId => ({
    question_id: questionId,
    answer: newData[questionId] ?? null,
  }))

  emit('update:modelValue', responses)
}, { deep: true })

const isConditionMet = (rule: ConditionalRule) => {
  const fieldValue = formData.value[rule.triggerQuestionId]
  const conditionValue = rule.value

  if (rule.operator === 'equals')
    return fieldValue === conditionValue
  if (rule.operator === 'not_equals')
    return fieldValue !== conditionValue
  if (rule.operator === 'contains') {
    return fieldValue !== undefined && fieldValue !== null
      && String(fieldValue).includes(String(conditionValue))
  }
  if (rule.operator === 'greater_than')
    return Number(fieldValue) > Number(conditionValue)
  if (rule.operator === 'less_than')
    return Number(fieldValue) < Number(conditionValue)

  return true
}

// Check if conditional logic should show this question
const shouldShowQuestion = (question: CampFormQuestion) => {
  if (!question.conditionalRule)
    return true

  const rule = question.conditionalRule as ConditionalRule
  const conditionMet = isConditionMet(rule)

  if (rule.action === 'show')
    return conditionMet
  if (rule.action === 'hide')
    return !conditionMet

  return true
}

const isQuestionRequired = (question: CampFormQuestion) => {
  if (!shouldShowQuestion(question))
    return false

  // Check both camelCase and snake_case for compatibility
  const required = question.isRequired || (question as unknown as { is_required?: boolean }).is_required

  if (required)
    return true

  const rule = question.conditionalRule as ConditionalRule | undefined
  if (rule && rule.action === 'require')
    return isConditionMet(rule)

  return false
}

const getQuestionLabel = (question: CampFormQuestion) => question.label || 'Dit veld'

const getLabelWithRequired = (question: CampFormQuestion) => {
  return `${getQuestionLabel(question)}${isQuestionRequired(question) ? ' *' : ''}`
}

const isAnswerEmpty = (question: CampFormQuestion, value: FormResponse['answer'] | undefined) => {
  if (question.questionType === 'checkbox')
    return !Array.isArray(value) || value.length === 0

  return value === undefined || value === null || value === ''
}

const getRequiredError = (question: CampFormQuestion) => {
  if (!isQuestionRequired(question))
    return null

  // Skip required validation when user indicated no RRN available
  if (question.questionType === 'rijksregisternummer' && noRRNFields.value[question.id])
    return null

  const value = formData.value[question.id]
  if (isAnswerEmpty(question, value))
    return `${getQuestionLabel(question)} is verplicht`

  return null
}

const getFieldErrorMessages = (question: CampFormQuestion) => {
  if (!shouldShowQuestion(question) || isContentElement(question.questionType))
    return []

  const errorMessages: string[] = []
  const requiredError = getRequiredError(question)

  if (requiredError)
    errorMessages.push(requiredError)

  const rijksregisterError = rijksregisterErrors.value[question.id]
  if (question.questionType === 'rijksregisternummer' && rijksregisterError)
    errorMessages.push(rijksregisterError)

  if (!showValidationErrors.value && !touchedFields.value[question.id])
    return []

  return errorMessages
}

const markTouched = (questionId: string) => {
  touchedFields.value[questionId] = true
}

// Validate rijksregisternummer on blur
const validateRijksregister = (questionId: string) => {
  if (noRRNFields.value[questionId]) {
    rijksregisterErrors.value[questionId] = null

    return
  }

  const value = formData.value[questionId]
  if (!value) {
    rijksregisterErrors.value[questionId] = null

    return
  }

  if (typeof value !== 'string') {
    rijksregisterErrors.value[questionId] = 'Ongeldig rijksregisternummer'

    return
  }

  const result = validateRijksregisternummer(value)
  if (result.isValid) {
    formData.value[questionId] = result.formattedNumber ?? value
    rijksregisterErrors.value[questionId] = null
  }
  else {
    rijksregisterErrors.value[questionId] = result.error || 'Ongeldig rijksregisternummer'
  }
}

// Format rijksregisternummer as user types
const onRijksregisterInput = (questionId: string, event: Event) => {
  const input = event.target as HTMLInputElement
  const value = input.value.replace(/\D/g, '')

  if (value.length <= 11) {
    let formatted = ''
    if (value.length > 0)
      formatted += value.substring(0, Math.min(2, value.length))
    if (value.length > 2)
      formatted += `.${value.substring(2, Math.min(4, value.length))}`
    if (value.length > 4)
      formatted += `.${value.substring(4, Math.min(6, value.length))}`
    if (value.length > 6)
      formatted += `-${value.substring(6, Math.min(9, value.length))}`
    if (value.length > 9)
      formatted += `.${value.substring(9, 11)}`

    formData.value[questionId] = formatted
  }
}

// Get title tag for title elements
const getTitleTag = (size?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6') => {
  return size || 'h3'
}

// Get typography class for title elements (smaller in checkout)
const getTitleClass = (size?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6') => {
  const tag = getTitleTag(size)

  if (tag === 'h1' || tag === 'h2')
    return 'text-h5'
  if (tag === 'h3')
    return 'text-h6'
  if (tag === 'h4')
    return 'text-subtitle-1'

  return 'text-subtitle-2'
}

// Validate all required fields and return validation status
const validate = (): { isValid: boolean, errors: string[] } => {
  const errors: string[] = []

  showValidationErrors.value = true

  props.forms.forEach(form => {
    form.questions.forEach(question => {
      if (!shouldShowQuestion(question))
        return

      if (isContentElement(question.questionType))
        return

      if (isBaseFieldQuestion(question.label))
        return

      const value = formData.value[question.id]
      const requiredError = getRequiredError(question)
      if (requiredError)
        errors.push(requiredError)

      // Check rijksregisternummer validation (skip when no-RRN is indicated)
      if (question.questionType === 'rijksregisternummer' && value && !noRRNFields.value[question.id]) {
        if (typeof value === 'string') {
          const result = validateRijksregisternummer(value)
          if (!result.isValid)
            errors.push(`${getQuestionLabel(question)}: ${result.error}`)
        }
        else {
          errors.push(`${getQuestionLabel(question)}: Ongeldig rijksregisternummer`)
        }
      }
    })
  })

  return {
    isValid: errors.length === 0,
    errors,
  }
}

// Expose validate method for parent components
defineExpose({ validate })
</script>

<template>
  <div class="checkout-attendee-form">
    <template
      v-for="form in forms"
      :key="form.id"
    >
      <VCardText>
        <div class="text-subtitle-2 font-weight-medium mb-3">
          Extra info
        </div>

        <!-- Form Questions -->
        <VRow class="checkout-attendee-form__row">
          <template
            v-for="question in form.questions"
            :key="question.id"
          >
            <VCol
              v-if="shouldShowQuestion(question)"
              cols="12"
              :md="isContentElement(question.questionType) ? 12 : 6"
            >
              <!-- Title Element -->
              <component
                :is="getTitleTag(question.titleSize)"
                v-if="question.questionType === 'title'"
                class="mb-4"
                :class="getTitleClass(question.titleSize)"
              >
                {{ question.content || question.label || question.description || '' }}
              </component>

              <!-- Paragraph Element -->
              <p
                v-else-if="question.questionType === 'paragraph'"
                class="text-body-1 mb-4"
              >
                {{ question.content || question.label || question.description || '' }}
              </p>

              <!-- Link Element -->
              <div
                v-else-if="question.questionType === 'link'"
                class="mb-4"
              >
                <VBtn
                  :href="question.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="text"
                  color="primary"
                  append-icon="bx-link-external"
                >
                  {{ question.linkText || 'Link' }}
                </VBtn>
              </div>

              <!-- Image Element -->
              <div
                v-else-if="question.questionType === 'image'"
                class="mb-4"
              >
                <VImg
                  :src="question.url"
                  :alt="question.imageAlt"
                  max-width="400"
                  class="rounded"
                />
              </div>

              <!-- Separator Element -->
              <VDivider
                v-else-if="question.questionType === 'separator'"
                class="my-6"
              />

              <!-- Input Fields -->
              <template v-else>
                <!-- Text Input -->
                <VTextField
                  v-if="question.questionType === 'text' && !isBaseFieldQuestion(question.label)"
                  v-model="formData[question.id]"
                  :label="getLabelWithRequired(question)"
                  :disabled="disabled"
                  :hint="question.helpText"
                  persistent-hint
                  :error-messages="getFieldErrorMessages(question)"
                  @blur="markTouched(question.id)"
                />

                <!-- Email Input -->
                <VTextField
                  v-else-if="question.questionType === 'email' && !isBaseFieldQuestion(question.label)"
                  v-model="formData[question.id]"
                  :label="getLabelWithRequired(question)"
                  :disabled="disabled"
                  type="email"
                  :hint="question.helpText"
                  persistent-hint
                  :error-messages="getFieldErrorMessages(question)"
                  @blur="markTouched(question.id)"
                />

                <!-- Tel Input -->
                <VTextField
                  v-else-if="question.questionType === 'tel' && !isBaseFieldQuestion(question.label)"
                  v-model="formData[question.id]"
                  :label="getLabelWithRequired(question)"
                  :disabled="disabled"
                  type="tel"
                  :hint="question.helpText"
                  persistent-hint
                  :error-messages="getFieldErrorMessages(question)"
                  @blur="markTouched(question.id)"
                />

                <!-- Rijksregisternummer Input -->
                <div v-else-if="question.questionType === 'rijksregisternummer' && !isBaseFieldQuestion(question.label)">
                  <VTextField
                    v-model="formData[question.id]"
                    :label="getLabelWithRequired(question)"
                    :disabled="disabled || noRRNFields[question.id]"
                    :error-messages="noRRNFields[question.id] ? [] : getFieldErrorMessages(question)"
                    maxlength="15"
                    @input="onRijksregisterInput(question.id, $event)"
                    @blur="() => { markTouched(question.id); validateRijksregister(question.id) }"
                  >
                    <template #append-inner>
                      <VIcon
                        icon="bx-info-circle"
                        size="20"
                        color="info"
                        class="cursor-pointer"
                      />
                      <VTooltip
                        activator="parent"
                        location="top"
                        max-width="300"
                      >
                        <span style="color: white;">
                          Het rijksregisternummer helpt bij het identificeren van deelnemers voor certificaten en terugkerende boekingen.
                        </span>
                      </VTooltip>
                    </template>
                  </VTextField>
                  <p class="text-caption text-medium-emphasis mt-1">
                    Formaat: JJ.MM.DD-XXX.CC (bijv. 90.01.15-123.45)
                  </p>
                  <VCheckbox
                    v-model="noRRNFields[question.id]"
                    :disabled="disabled"
                    density="compact"
                    hide-details
                    class="mt-1"
                    @update:model-value="(checked) => {
                      if (checked) {
                        formData[question.id] = null
                        rijksregisterErrors[question.id] = null
                      }
                    }"
                  >
                    <template #label>
                      <span class="text-caption">
                        Mijn kind heeft geen Belgisch rijksregisternummer
                      </span>
                    </template>
                  </VCheckbox>
                  <VAlert
                    v-if="noRRNFields[question.id]"
                    type="warning"
                    variant="tonal"
                    density="compact"
                    class="mt-2"
                  >
                    <span class="text-caption">
                      Zonder rijksregisternummer kunnen wij geen fiscaal attest of mutualiteitsattest opstellen voor uw kind.
                    </span>
                  </VAlert>
                </div>

                <!-- Number Input -->
                <VTextField
                  v-else-if="question.questionType === 'number' && !isBaseFieldQuestion(question.label)"
                  v-model="formData[question.id]"
                  :label="getLabelWithRequired(question)"
                  :disabled="disabled"
                  type="number"
                  :hint="question.helpText"
                  persistent-hint
                  :error-messages="getFieldErrorMessages(question)"
                  @blur="markTouched(question.id)"
                />

                <!-- Date Input -->
                <AppDateTimePicker
                  v-else-if="question.questionType === 'date' && !isBaseFieldQuestion(question.label)"
                  :model-value="formData[question.id]"
                  :label="getLabelWithRequired(question)"
                  :disabled="disabled"
                  :config="{ dateFormat: 'd-m-Y' }"
                  :error-messages="getFieldErrorMessages(question)"
                  @update:model-value="value => { formData[question.id] = value; markTouched(question.id) }"
                />

                <!-- Textarea -->
                <VTextarea
                  v-else-if="question.questionType === 'textarea' && !isBaseFieldQuestion(question.label)"
                  v-model="formData[question.id]"
                  :label="getLabelWithRequired(question)"
                  :disabled="disabled"
                  rows="3"
                  :hint="question.helpText"
                  persistent-hint
                  :error-messages="getFieldErrorMessages(question)"
                  @blur="markTouched(question.id)"
                />

                <!-- Select/Dropdown -->
                <VSelect
                  v-else-if="question.questionType === 'select' && !isBaseFieldQuestion(question.label)"
                  :model-value="getSelectValue(question.id)"
                  :label="getLabelWithRequired(question)"
                  :items="question.options || []"
                  :disabled="disabled"
                  :hint="question.helpText"
                  persistent-hint
                  :error-messages="getFieldErrorMessages(question)"
                  @update:model-value="value => { setSelectValue(question.id, value); markTouched(question.id) }"
                />

                <!-- Checkbox (Multiple Choice) -->
                <div v-else-if="question.questionType === 'checkbox' && !isBaseFieldQuestion(question.label)">
                  <label class="text-body-1 d-block mb-2">
                    {{ getLabelWithRequired(question) }}
                  </label>
                  <div
                    v-for="(option, idx) in question.options"
                    :key="idx"
                    class="mb-1"
                  >
                    <VCheckbox
                      v-model="formData[question.id]"
                      :label="option"
                      :value="option"
                      :disabled="disabled"
                      hide-details
                      density="compact"
                      @update:model-value="() => markTouched(question.id)"
                    />
                  </div>
                  <div
                    v-if="getFieldErrorMessages(question).length"
                    class="text-caption text-error mt-1"
                  >
                    <div v-for="(error, errorIndex) in getFieldErrorMessages(question)" :key="errorIndex">
                      {{ error }}
                    </div>
                  </div>
                  <p
                    v-if="question.helpText"
                    class="text-caption text-medium-emphasis mt-1"
                  >
                    {{ question.helpText }}
                  </p>
                </div>

                <!-- Radio Buttons -->
                <div v-else-if="question.questionType === 'radio' && !isBaseFieldQuestion(question.label)">
                  <label class="text-body-1 d-block mb-2">
                    {{ getLabelWithRequired(question) }}
                  </label>
                  <VRadioGroup
                    v-model="formData[question.id]"
                    :disabled="disabled"
                    hide-details
                    :error-messages="getFieldErrorMessages(question)"
                    @update:model-value="() => markTouched(question.id)"
                  >
                    <VRadio
                      v-for="(option, idx) in question.options"
                      :key="idx"
                      :label="option"
                      :value="option"
                    />
                  </VRadioGroup>
                  <p
                    v-if="question.helpText"
                    class="text-caption text-medium-emphasis mt-1"
                  >
                    {{ question.helpText }}
                  </p>
                </div>
              </template>
            </VCol>
          </template>
        </VRow>
      </VCardText>
      <!-- Divider between forms -->
    </template>

    <!-- No forms message -->
    <VAlert
      v-if="forms.length === 0"
      type="info"
      variant="tonal"
    >
      <template #prepend>
        <VIcon icon="bx-info-circle" />
      </template>
      Er zijn geen aanvullende formulieren voor dit kamp.
    </VAlert>
  </div>
</template>
